import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  Card, Form, Row, Input, Col, Switch, Button, Space, message, Upload,
} from 'antd';
import { ArrowLeftOutlined, UploadOutlined } from '@ant-design/icons';
import Editor from '../../../components/editor';
import { GET_TOPIC, REMOVE_UPLOAD, UPDATE_TOPIC } from '../../../graphql';
import { uploadFiles } from '../../../upload';
import ViewSpinner from '../../../components/viewSpinner/ViewSpinner';
import './styles.less';

const { Item: FormItem, useForm } = Form;
const EditTopic = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [form] = useForm();
  const params = new URLSearchParams(search);
  const topicId = params.get('topic');
  // A topic is addressed within its subject, so both ids travel together.
  const subjectId = params.get('sub');
  const [fileDeleteCache, setFileDeleteCache] = useState([]);
  const { data: topicPayload, loading: loadingTopic } = useQuery(GET_TOPIC, {
    variables: { subjectId, id: topicId },
  });
  const [updateTopic, { loading }] = useMutation(UPDATE_TOPIC);
  const [removeUpload] = useMutation(REMOVE_UPLOAD);
  const client = useApolloClient();
  const [uploadLoading, setUploadLoading] = useState(false);

  const [content, setContent] = useState('');

  const handleFormSubmit = async (inputs) => {
    const {
      attachment, status, title, video,
    } = inputs;

    try {
      // `topicNo` is allocated server-side, so it is no longer editable here.
      await updateTopic({
        variables: {
          subjectId,
          id: topicId,
          input: {
            title,
            video,
            content,
            status: status ? 'PUBLISHED' : 'DRAFT',
          },
        },
      });

      // Attachments are their own operations now rather than a nested
      // connect/disconnect riding along on the update.
      const added = (attachment?.fileList ?? [])
        .filter((file) => file.originFileObj)
        .map((file) => file.originFileObj);

      if (added.length > 0) {
        setUploadLoading(true);
        try {
          await uploadFiles({
            client,
            files: added,
            target: 'TOPIC',
            targetId: `${subjectId}/${topicId}`,
          });
        } finally {
          setUploadLoading(false);
        }
      }

      await Promise.all(fileDeleteCache.map(({ id }) => removeUpload({
        variables: { target: 'TOPIC', targetId: `${subjectId}/${topicId}`, uploadId: id },
      })));

      message.success('Topic updated successfully');
      navigate(-1);
    } catch (err) {
      message.error(err.message || 'Could not update topic');
    }
  };

  useEffect(() => {
    if (!loadingTopic && topicPayload) {
      const { topic } = topicPayload;
      const status = topic.status !== 'DRAFT';
      form.setFields([
        { name: 'title', value: topic.title },
        { name: 'video', value: topic.video },
        { name: 'status', value: status },
      ]);
      setContent(topic.content);
    }
  }, [topicPayload, loadingTopic, form]);

  const makeDefaultFileList = () => {
    const { uploads } = topicPayload.topic;
    const fileList = uploads.map(({
      id, url, filename,
    }) => ({
      uid: id,
      name: `${filename.slice(11) || 'File'}`,
      status: 'done',
      url,
    }));
    return fileList;
  };

  return (
    <div>
      <Card className="gx-card gx-p-1 new-topic">
        {!loadingTopic && topicPayload && (
        <Form form={form} name="update-topic" requiredMark="optional" onFinish={handleFormSubmit} layout="vertical">
          <Row className="no-flex-column">
            <Col span={24} className="gx-mb-3">
              <div className="heading">
                <h3>Update Topic</h3>
                <Button onClick={() => navigate(-1)} size="middle" shape="circle" icon={<ArrowLeftOutlined />} />
              </div>
            </Col>
            <Col span={24}>
              <FormItem name="status" valuePropName="checked" required={false}>
                <Switch checkedChildren="Publish" unCheckedChildren="Draft" />
              </FormItem>
            </Col>
            <Col span={24}>
              <FormItem name="title" required label="Title">
                <Input placeholder="enter a title" />
              </FormItem>
            </Col>
            <Col xm={24} md={14}>
              <FormItem name="video" required={false} label="Video Id">
                <Input placeholder="e.g ZZUyshYtsw" />
              </FormItem>
            </Col>
          </Row>
          <Col className="gx-mt-2" span={24}>
            <Editor content={content} setContent={setContent} />
          </Col>
          <Col className="gx-mt-4" span={24}>
            <FormItem
              label="Attachment"
              valuePropName="file"
              name="attachment"
            >
              <Upload
                listType="picture"
                beforeUpload={() => false}
                onRemove={(file) => setFileDeleteCache([...fileDeleteCache, { id: file.uid }])}
                defaultFileList={makeDefaultFileList()}
              >
                <Button icon={<UploadOutlined />}>Attach files</Button>
              </Upload>
            </FormItem>
          </Col>
          <Col className="gx-mt-4" span={24}>
            <Space>
              <Button onClick={() => navigate(-1)}>Cancel</Button>
              <Button loading={loading || uploadLoading} htmlType="submit" type="primary">Save Topic</Button>
            </Space>
          </Col>
        </Form>
        )}
        {loadingTopic && <ViewSpinner />}
      </Card>
    </div>
  );
};

export default EditTopic;
